// Quelle: /opt/webgis/frontend/src/app/AvatarMenu.tsx (Stand 2026-09-29)
// Geändert (SPEC 03 §7.2.1 Punkt 8): Initialen aus `anzeigename` (Name ≤ 559 px ausgeblendet);
// Menü mit Name + E-Mail (mono) + Rollenchip; „Anzeigename ändern“ (Modal, PATCH /api/me);
// „Verwaltung“ nur für admin; „Abmelden“ (POST /api/auth/logout → Anmeldung). Das Menü ist der
// Hausbaustein `Menue` (role="menu", Pfeiltasten, Fokus zurück).
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { setzeAnzeigename } from "../../api/client";
import type { Ich } from "../../api/typen";
import { Btn, Chip, Field, Input, Menue, MenuePunkt, Modal } from "../ui";
import { ApiError } from "../../lib/fetchJson";
import { ROLLE_LABEL, TX } from "../../lib/texte";
import { useTheme } from "../../lib/theme";
import { toast } from "../../store/useStore";
import { useAuthStore } from "../../store/useAuthStore";

export function initialen(name: string): string {
  const teile = name.trim().split(/[\s.@_-]+/).filter(Boolean);
  const a = teile[0]?.[0] ?? "?";
  const b = teile.length > 1 ? (teile[teile.length - 1]?.[0] ?? "") : (teile[0]?.[1] ?? "");
  return (a + b).toUpperCase();
}

export function AvatarMenu({ ich }: { ich: Ich }) {
  const [offen, setOffen] = useState(false);
  const [namensModal, setNamensModal] = useState(false);
  const [name, setName] = useState(ich.anzeigename);
  const navigate = useNavigate();
  const abmelden = useAuthStore((s) => s.abmelden);
  const qc = useQueryClient();
  const { thema, umschalten } = useTheme();

  const speichern = useMutation({
    mutationFn: (n: string) => setzeAnzeigename(n),
    onSuccess: (neu) => {
      qc.setQueryData(["ich"], neu);
      setNamensModal(false);
      toast({ type: "success", msg: TX.shell.anzeigenameGespeichert });
    },
    onError: (e) => toast({ type: "error", msg: e instanceof ApiError ? e.message : TX.allgemein.laedtNicht }),
  });

  return (
    <div className="menue-anker" style={{ position: "relative" }}>
      <button
        type="button"
        className="avatar ring-focus"
        aria-haspopup="menu"
        aria-expanded={offen}
        aria-label={`Konto: ${ich.anzeigename}`}
        onClick={() => setOffen((o) => !o)}
      >
        <span className="av-name">{ich.anzeigename.split(" ")[0]}</span>
        <i aria-hidden="true">{initialen(ich.anzeigename)}</i>
      </button>
      <Menue open={offen} onClose={() => setOffen(false)} width={260} label="Konto">
        <div style={{ padding: "8px 9px 10px", display: "flex", flexDirection: "column", gap: 3 }} role="presentation">
          <p style={{ fontWeight: 600, fontSize: 14, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{ich.anzeigename}</p>
          <p className="mono" style={{ fontSize: 11.5, color: "var(--text-mute)", overflowWrap: "anywhere" }}>
            {ich.email}
          </p>
          <div style={{ marginTop: 4 }}>
            <Chip tone={ich.rolle === "admin" ? "teal" : "neutral"} style={{ padding: "2px 8px", fontSize: 10.5 }}>
              {ROLLE_LABEL[ich.rolle]}
            </Chip>
          </div>
        </div>
        <hr />
        {/* Das Thema: in der Kopfleiste ≤ 559 px dem Aufträge-Knopf gewichen — hier immer erreichbar. */}
        <MenuePunkt
          icon={thema === "dark" ? "sun" : "moon"}
          onClick={() => {
            setOffen(false);
            umschalten();
          }}
        >
          {thema === "dark" ? TX.shell.hell : TX.shell.dunkel}
        </MenuePunkt>
        <MenuePunkt
          icon="pencil-simple"
          onClick={() => {
            setOffen(false);
            setName(ich.anzeigename);
            setNamensModal(true);
          }}
        >
          {TX.shell.anzeigename}
        </MenuePunkt>
        {ich.rolle === "admin" && (
          <MenuePunkt
            icon="gear"
            onClick={() => {
              setOffen(false);
              navigate("/admin");
            }}
          >
            {TX.shell.verwaltung}
          </MenuePunkt>
        )}
        <MenuePunkt
          icon="sign-out"
          gefahr
          onClick={() => {
            setOffen(false);
            void abmelden().then(() => navigate("/anmelden", { replace: true }));
          }}
        >
          {TX.shell.abmelden}
        </MenuePunkt>
      </Menue>

      <Modal
        open={namensModal}
        onClose={() => setNamensModal(false)}
        title={TX.shell.anzeigename}
        icon="user-circle"
        width={440}
        footer={
          <>
            <Btn type="button" variant="ghost" onClick={() => setNamensModal(false)}>
              {TX.allgemein.abbrechen}
            </Btn>
            <Btn type="submit" form="anzeigename-form" disabled={!name.trim() || speichern.isPending}>
              {TX.allgemein.speichern}
            </Btn>
          </>
        }
      >
        <form
          id="anzeigename-form"
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) speichern.mutate(name.trim());
          }}
        >
          <Field label={TX.shell.anzeigenameFeld}>
            <Input value={name} maxLength={80} autoFocus onChange={(e) => setName(e.target.value)} />
          </Field>
        </form>
      </Modal>
    </div>
  );
}
